import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { moduleApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  ArrowLeft, Plus, MoreVertical, Pencil, Trash2, Search,
  FileText, Settings, AlertTriangle, Lock, Mail, MailCheck, Columns3, CalendarDays, Upload, BookOpen,
} from "lucide-react";
import { formatEuro, mapReservationFromApi, reservationToApiPayload, type Board, type Reservation } from "./types";
import type { BoardColumn } from "./boardColumns";
import { BoardGrid, BoardTotals } from "./BoardGrid";
import { OriginLegend } from "./ReservationSourcesPanel";
import { BoardColumnsModal } from "./BoardColumnsModal";
import { ExportModal } from "./ExportModal";
import { useUndo } from "../ui/UndoBar";
import { NewReservationModal } from "./NewReservationModal";
import { BoardSettingsModal } from "./BoardSettingsModal";
import { SendAlertModal } from "./SendAlertModal";
import { AlertPreviewModal } from "./AlertPreviewModal";
import { ReservationImportModal } from "./ReservationImportModal";
import { LivreLienModal } from "./LivreLienModal";
import { useReservationImport } from "./useReservationImport";
import { exportPdf } from "./exportUtils";
import { useConfirm } from '../ui/ConfirmProvider';

export function BoardDetail({
  onBack,
  board,
  takenByOptions,
  cashedByOptions,
  onUpdateSettings,
  onUpdateColumns,
  onDeleteBoard,
}: {
  onBack: () => void;
  board: Board | undefined;
  takenByOptions: string[];
  cashedByOptions: string[];
  onUpdateSettings: (settings: { title: string; takenByOptions: string[]; cashedByOptions: string[] }) => void;
  onUpdateColumns: (columns: BoardColumn[]) => Promise<void>;
  onDeleteBoard: (id: number, summary: React.ReactNode) => void;
}) {
  const { user } = useAuth();
  const confirmAction = useConfirm();
  const undo = useUndo();
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isSendAlertModalOpen, setIsSendAlertModalOpen] = useState(false);
  const [previewAlertReservationId, setPreviewAlertReservationId] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isLivreModalOpen, setIsLivreModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const isArchived = !!board?.isArchived;

  const loadReservations = async () => {
    if (!board?.id) return;
    try {
      const data = await moduleApi.getExceptionServiceBoardReservations(board.id);
      if (Array.isArray(data)) setReservations(data.map((r: any) => mapReservationFromApi(r, board.title)));
    } catch (e) {
      console.error("Failed to load reservations", e);
    }
  };

  const { importing: importingReservations, importRows: importReservationRows } = useReservationImport(board?.id, board?.columns || [], onUpdateColumns, loadReservations);

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.id, board?.title]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredReservations = normalizedSearch
    ? reservations.filter((r) => `${r.lastName} ${r.firstName} ${r.phone} ${r.comment}`.toLowerCase().includes(normalizedSearch))
    : reservations;

  const totalsSource = filteredReservations;
  const totalAdults = totalsSource.reduce((sum, r) => sum + (Number(r.adults) || 0), 0);
  const totalChildren = totalsSource.reduce((sum, r) => sum + (Number(r.children) || 0), 0);
  const totalCovers = totalAdults + totalChildren;
  const totalAdultsAmount = totalsSource.reduce((sum, r) => sum + (Number(r.adults) || 0) * (Number(r.tarifAdult) || 0), 0);
  const totalChildrenAmount = totalsSource.reduce((sum, r) => sum + (Number(r.children) || 0) * (Number(r.tarifChild) || 0), 0);
  const totalTheoretical = totalAdultsAmount + totalChildrenAmount;
  const totalCb = totalsSource.reduce((sum, r) => sum + (Number(r.cb) || 0), 0);
  const totalAmex = totalsSource.reduce((sum, r) => sum + (Number(r.amex) || 0), 0);
  const totalCash = totalsSource.reduce((sum, r) => sum + (Number(r.cash) || 0), 0);
  const totalCheque = totalsSource.reduce((sum, r) => sum + (Number(r.cheque) || 0), 0);
  const totalZen = totalsSource.reduce((sum, r) => sum + (Number(r.zen) || 0), 0);
  const totalVirm = totalsSource.reduce((sum, r) => sum + (Number(r.virm) || 0), 0);
  const totalEncaisse = totalCb + totalAmex + totalCash + totalCheque + totalZen + totalVirm;
  const totalResteAPayer = totalTheoretical - totalEncaisse;

  /** Combien de lignes viennent d'une plateforme — le badge disparaît quand il n'y en a aucune. */
  const importedCount = reservations.filter((r) => r.source && r.source !== 'manual').length;

  const coversBySize: { [key: number]: number } = {};
  filteredReservations.forEach((r) => {
    const covers = r.adults + r.children;
    coversBySize[covers] = (coversBySize[covers] || 0) + 1;
  });

  const handleSaveReservation = async (reservation: Reservation) => {
    if (!board?.id) return;
    try {
      if (editingReservation) {
        await moduleApi.updateExceptionServiceReservation(editingReservation.id, reservationToApiPayload(reservation));
        setReservations((prev) => prev.map((r) => (r.id === editingReservation.id ? { ...reservation, id: editingReservation.id } : r)));
      } else {
        const res: any = await moduleApi.createExceptionServiceReservation(board.id, reservationToApiPayload(reservation));
        setReservations((prev) => [...prev, { ...reservation, id: res.id }]);
      }
    } catch (e) {
      console.error("Failed to save reservation", e);
    }
  };

  /**
   * Suppression d'une ligne : immédiate, avec 10 secondes pour annuler (passation §6). Demander
   * confirmation à chaque suppression finit par être cliqué sans lire ; l'annulation, elle, rattrape
   * le geste malheureux sans jamais ralentir le geste voulu.
   */
  const handleDeleteReservation = async (reservation: Reservation) => {
    setReservations((prev) => prev.filter((x) => x.id !== reservation.id));
    try {
      await moduleApi.deleteExceptionServiceReservation(reservation.id);
    } catch (e) {
      console.error("Failed to delete reservation", e);
      loadReservations();
      return;
    }
    const nom = `${reservation.lastName} ${reservation.firstName}`.trim() || 'sans nom';
    undo.push({
      message: `Réservation de ${nom} supprimée${reservation.time ? ` — ${reservation.time}` : ''}`,
      undoneMessage: 'Réservation rétablie',
      // Rétablir = recréer : la ligne d'origine est déjà partie de la base, son ancien identifiant
      // avec elle. On recharge donc depuis le serveur pour repartir de l'id réellement attribué.
      undo: async () => {
        if (!board?.id) return;
        try {
          await moduleApi.createExceptionServiceReservation(board.id, reservationToApiPayload(reservation));
        } catch (e) {
          console.error("Failed to restore reservation", e);
        }
        loadReservations();
      },
    });
  };

  const handleDeleteAllReservations = async () => {
    if (!(await confirmAction({ title: 'Supprimer toutes les réservations ?', description: <>Toutes les réservations de ce tableau seront effacées définitivement.</>, confirmLabel: 'Tout supprimer' }))) return;
    try {
      await Promise.all(reservations.map((r) => moduleApi.deleteExceptionServiceReservation(r.id)));
    } catch (e) {
      console.error("Failed to delete all reservations", e);
    }
    setReservations([]);
  };

  return (
    <div className="space-y-6 pb-24">
      <div>
        <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-content-muted hover:text-content transition-colors mb-3">
          <ArrowLeft size={16} /> Retour à la liste des tableaux
        </button>
        <div className="flex items-center gap-2.5 flex-wrap">
          <h1 className="text-2xl font-black text-content">{board?.title ?? "Tableau"}</h1>
          {board?.serviceDate && (
            <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-accent-soft border border-accent-border text-accent-text text-[11.5px] font-bold shrink-0">
              <CalendarDays size={12} className="shrink-0" />
              {new Date(`${board.serviceDate}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          )}
          {importedCount > 0 && (
            <span className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full bg-state-info-soft border border-state-info/30 text-state-info text-[10px] font-bold shrink-0">
              <span className="w-1 h-1 rounded-full bg-state-info" />{importedCount} importées
            </span>
          )}
          {isArchived && (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface-soft border border-border text-content-muted text-xs font-bold">
              <Lock size={12} /> Archivé — lecture seule
            </span>
          )}
        </div>
        {/* La règle de datation est annoncée là où on ajoute des lignes, pas dans une aide séparée :
            c'est le seul endroit où elle change quelque chose pour l'utilisateur. */}
        <p className="text-[11.5px] text-content-muted tabular-nums mt-1">
          {reservations.length} réservation{reservations.length > 1 ? 's' : ''} · {totalCovers} couverts
          {board?.serviceDate && <> · toute nouvelle ligne est datée du {new Date(`${board.serviceDate}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</>}
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
          <input
            type="text"
            placeholder="Rechercher une réservation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-surface border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          {!isArchived && (
            <>
              <button onClick={() => setIsColumnsModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                <Columns3 size={16} /> Colonnes
              </button>
              <button onClick={() => setIsSendAlertModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                <Mail size={16} /> Envoyer une alerte au client
              </button>
              <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                <Upload size={16} /> Importer un CSV
              </button>
              <button onClick={() => setIsLivreModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                <BookOpen size={16} /> Livre de réservations
              </button>
              <button onClick={() => { setEditingReservation(null); setIsModalOpen(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all">
                <Plus size={16} /> Nouvelle réservation
              </button>
            </>
          )}
          <div className="relative">
            <button onClick={() => setIsDropdownOpen((v) => !v)} className="p-2.5 rounded-xl bg-surface-soft hover:bg-surface border border-border text-content-muted hover:text-content transition-all">
              <MoreVertical size={18} />
            </button>
            <AnimatePresence>
              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 top-[calc(100%+8px)] w-64 bg-surface border border-border rounded-2xl shadow-2xl z-50 p-2 space-y-0.5"
                  >
                    <button onClick={() => { setIsDropdownOpen(false); setIsExportModalOpen(true); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-content hover:bg-surface-soft transition-all">
                      <FileText size={16} className="text-content-muted" /> Exporter (Excel ou PDF)
                    </button>
                    {!isArchived && (
                      <>
                        <div className="h-px bg-border my-1" />
                        <button onClick={() => { setIsDropdownOpen(false); setIsSettingsModalOpen(true); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-content hover:bg-surface-soft transition-all">
                          <Settings size={16} /> Paramètres du tableau
                        </button>
                        <div className="h-px bg-border my-1" />
                        <button onClick={() => { setIsDropdownOpen(false); handleDeleteAllReservations(); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-state-danger hover:bg-state-danger-soft transition-all">
                          <AlertTriangle size={16} /> Supprimer toutes les réservations
                        </button>
                        {/* La confirmation NOMME ce qui disparaît — « 12 réservations et 3 740 € » — parce
                            qu'un « êtes-vous sûr ? » générique se clique sans lire (passation §6). */}
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            if (!board) return;
                            onDeleteBoard(board.id, (
                              <>{reservations.length} réservation{reservations.length > 1 ? 's' : ''} et {formatEuro(totalEncaisse)} d'encaissements seront effacés. Cette action est définitive.</>
                            ));
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-state-danger hover:bg-state-danger-soft transition-all">
                          <Trash2 size={16} /> Supprimer le tableau
                        </button>
                      </>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {Object.keys(coversBySize).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(coversBySize).sort(([a], [b]) => Number(a) - Number(b)).map(([size, count]) => (
            <div key={size} className="bg-brand-500/10 border border-brand-500/20 rounded-xl px-3 py-2 text-center">
              <span className="text-[11px] text-content-muted font-bold uppercase tracking-wide mr-1.5">Table {size}:</span>
              <span className="text-brand-600 dark:text-brand-400 font-black text-sm">{count}</span>
            </div>
          ))}
        </div>
      )}


      {board && (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <BoardGrid
            board={board}
            reservations={filteredReservations}
            renderRowActions={isArchived ? undefined : (r) => (
              <>
                {r.email && (
                  r.alertSentAt ? (
                    <button onClick={() => setPreviewAlertReservationId(r.id)}
                      title={`Alerte envoyée le ${new Date(r.alertSentAt).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — cliquer pour voir l'e-mail`}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-state-ok hover:bg-state-ok-soft transition-colors">
                      <MailCheck size={13} />
                    </button>
                  ) : (
                    <span title="N'a pas encore reçu l'alerte groupée" className="w-7 h-7 flex items-center justify-center text-content-muted opacity-50">
                      <Mail size={13} />
                    </span>
                  )
                )}
                <button onClick={() => { setEditingReservation(r); setIsModalOpen(true); }} title="Modifier"
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-content-muted hover:bg-surface-soft hover:text-content transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => handleDeleteReservation(r)} title="Supprimer"
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-state-danger/80 hover:bg-state-danger-soft hover:text-state-danger transition-colors">
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
          <OriginLegend />
          <BoardTotals board={board} reservations={filteredReservations} />
        </div>
      )}

      {!isArchived && (
        <button
          onClick={() => { setEditingReservation(null); setIsModalOpen(true); }}
          className="fixed right-6 bottom-8 w-14 h-14 rounded-full bg-brand-600 hover:bg-brand-700 text-brand-fg shadow-2xl flex items-center justify-center transition-all z-30"
        >
          <Plus size={24} />
        </button>
      )}

      {isModalOpen && (
        <NewReservationModal
          onClose={() => setIsModalOpen(false)}
          initialData={editingReservation}
          onSave={handleSaveReservation}
          takenByOptions={takenByOptions}
          cashedByOptions={cashedByOptions}
        />
      )}
      {isSendAlertModalOpen && board?.id && (
        <SendAlertModal
          boardId={board.id}
          onClose={() => setIsSendAlertModalOpen(false)}
          onSent={loadReservations}
        />
      )}
      {isImportModalOpen && board?.id && (
        <ReservationImportModal
          boardId={board.id}
          boardColumns={board.columns}
          importing={importingReservations}
          onImport={importReservationRows}
          onClose={() => setIsImportModalOpen(false)}
        />
      )}
      {isLivreModalOpen && board?.id && (
        <LivreLienModal boardId={board.id} onClose={() => setIsLivreModalOpen(false)} onSynced={loadReservations} />
      )}
      {previewAlertReservationId !== null && (
        <AlertPreviewModal reservationId={previewAlertReservationId} onClose={() => setPreviewAlertReservationId(null)} />
      )}
      {isExportModalOpen && board && (
        <ExportModal
          board={board}
          reservations={filteredReservations}
          companyName={user?.company_name || "Votre établissement"}
          onClose={() => setIsExportModalOpen(false)}
          onExportPdf={() => exportPdf(board, filteredReservations, user?.theme_color || undefined)}
        />
      )}
      {isColumnsModalOpen && board && (
        <BoardColumnsModal
          boardTitle={board.title}
          columns={board.columns}
          saving={false}
          onClose={() => setIsColumnsModalOpen(false)}
          onSave={(columns) => { onUpdateColumns(columns); setIsColumnsModalOpen(false); }}
        />
      )}
      {isSettingsModalOpen && (
        <BoardSettingsModal
          onClose={() => setIsSettingsModalOpen(false)}
          initialTitle={board?.title ?? ""}
          initialTakenBy={takenByOptions}
          initialCashedBy={cashedByOptions}
          onSave={(settings) => { onUpdateSettings(settings); setIsSettingsModalOpen(false); }}
        />
      )}
    </div>
  );
}
